/**
 * Prepara uma imagem escolhida pelo agente para caber numa mensagem.
 *
 * O anexo viaja como data URI dentro da própria mensagem — é assim que as imagens recebidas já
 * são guardadas, e não há object storage neste deploy. Isso impõe um teto, e o teto é o motivo
 * deste arquivo existir: uma foto de celular tem 4 a 12 MB e viraria 16 MB em base64, então sem
 * redução quase toda tentativa de anexar falharia.
 *
 * Só reduz o que precisa. Um PNG pequeno passa intacto: recomprimir para JPEG por precaução
 * jogaria fora a transparência de um print ou de uma arte sem ganho nenhum.
 */

/** Teto do data URI. O servidor aceita 7 MB; a folga cobre o cabeçalho e o arredondamento. */
const MAX_DATA_URL = 6_500_000
/** Lado maior depois da redução. 1600px é mais que qualquer canal exibe e ainda dá zoom. */
const MAX_SIDE = 1600

export interface PreparedImage {
  dataUrl: string
  filename: string
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader()
    r.onload = () => resolve(String(r.result))
    r.onerror = () => reject(new Error('Não consegui ler o arquivo.'))
    r.readAsDataURL(file)
  })
}

export function isImage(file: File): boolean {
  return /^image\/(png|jpe?g|webp|gif)$/i.test(file.type)
}

export async function prepareImage(file: File): Promise<PreparedImage> {
  if (!isImage(file)) throw new Error(`"${file.name}" não é uma imagem que os canais aceitem.`)

  const original = await readAsDataUrl(file)
  const bitmap = await createImageBitmap(file)
  const maior = Math.max(bitmap.width, bitmap.height)

  // Já cabe e já é pequena: vai como está, sem recomprimir.
  if (original.length <= MAX_DATA_URL && maior <= MAX_SIDE) {
    bitmap.close()
    return { dataUrl: original, filename: file.name }
  }

  const escala = Math.min(1, MAX_SIDE / maior)
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(bitmap.width * escala)
  canvas.height = Math.round(bitmap.height * escala)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('canvas indisponível')
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
  bitmap.close()

  // Baixa a qualidade só enquanto não couber. Uma foto de parede a 0,6 ainda mostra a textura
  // que o cliente precisa ver; sair direto num valor baixo estragaria as que caberiam a 0,85.
  for (const q of [0.85, 0.7, 0.6, 0.45]) {
    const out = canvas.toDataURL('image/jpeg', q)
    if (out.length <= MAX_DATA_URL) {
      return { dataUrl: out, filename: file.name.replace(/\.[^.]+$/, '') + '.jpg' }
    }
  }
  throw new Error(`"${file.name}" é grande demais mesmo depois de reduzida.`)
}

/** Todas as imagens de um DataTransfer — cobre tanto arrastar quanto colar. */
export function imagesFrom(dt: DataTransfer | null): File[] {
  if (!dt) return []
  const files = Array.from(dt.files ?? [])
  if (files.length) return files.filter(isImage)
  // Colar de um editor entrega a imagem em `items`, não em `files`.
  return Array.from(dt.items ?? [])
    .filter((i) => i.kind === 'file')
    .map((i) => i.getAsFile())
    .filter((f): f is File => Boolean(f) && isImage(f!))
}
